import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useRef,
  type ReactNode,
} from 'react';
import {
  applyTheme,
  resolveInitialTheme,
  storeTheme,
  readStoredTheme,
  clearStoredTheme,
  prefersDarkTheme,
} from '@src/theme/applyTheme';
import { useLocation } from 'react-router-dom';
import { currentLocalSeasonPreview } from './localSeasonPreview';
import { ThemeContext, type ThemeContextValue } from '@src/theme/ThemeContext';
import type { Theme } from '@src/theme/theme';

import type { SeasonPackage } from './types';
import { type Season, storeSeason } from './season';
import { loadSeason } from './registry';
import { preloadLandscape } from './preloadLandscape';
import { changeHomeAppearance } from './homeMotion';

export interface ThemeProviderProps {
  children: ReactNode;
  initialSeasonPackage: SeasonPackage;
  /** Initial resolved color for first paint / hydration, not a saved preference. */
  initialTheme?: Theme;
}

export function ThemeProvider({
  children,
  initialTheme,
  initialSeasonPackage,
}: ThemeProviderProps) {
  const [seasonPackage, setSeasonPackage] = useState(initialSeasonPackage);
  const [seasonOverride, setSeasonOverride] = useState<Season | null>(null);
  const { search } = useLocation();
  const preview = currentLocalSeasonPreview(search);
  const effectiveOverride = preview ?? seasonOverride;
  const [overridePackage, setOverridePackage] = useState<SeasonPackage | null>(null);
  useEffect(() => {
    let active = true;
    if (effectiveOverride)
      void loadSeason(effectiveOverride)
        .then((pkg) => {
          if (active) setOverridePackage(pkg);
        })
        .catch(() => {
          /* Keep the default if artwork cannot load. */
        });
    return () => {
      active = false;
    };
  }, [effectiveOverride]);
  const activePackage =
    effectiveOverride && overridePackage?.id === effectiveOverride ? overridePackage : seasonPackage;
  const request = useRef(0);
  const setSeason = useCallback(async (season: Season, options?: { persist?: boolean }) => {
    const current = ++request.current;
    const next = await loadSeason(season);
    await preloadLandscape(next, document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light');
    if (current !== request.current) return;
    if (options?.persist !== false) storeSeason(season);
    if (document.documentElement.dataset.season === season) setSeasonPackage(next);
    else await changeHomeAppearance(() => { if (current === request.current) setSeasonPackage(next); });
  }, []);

  useEffect(() => {
    const cancelPending = () => {
      request.current++;
    };
    return cancelPending;
  }, [initialSeasonPackage.id, setSeason]);

  const [theme, setThemeState] = useState<Theme>(() => initialTheme ?? resolveInitialTheme());

  const colorRequest = useRef(0);
  const changeColor = useCallback((next: Theme) => {
    const current = ++colorRequest.current;
    if (document.documentElement.dataset.theme === next) { setThemeState(next); return; }
    if (location.pathname !== '/' || !document.querySelector('[data-testid="home-main"]')) {
      applyTheme(next);
      setThemeState(next);
      return;
    }
    void preloadLandscape(activePackage, next).then(() => {
      if (current !== colorRequest.current) return;
      return changeHomeAppearance(() => {
        if (current !== colorRequest.current) return;
        applyTheme(next);
        setThemeState(next);
      });
    }).catch(() => { /* Keep the current, complete scenery if loading fails. */ });
  }, [activePackage]);

  const [preference, setPreferenceState] = useState<Theme | 'auto'>(
    // Boot passes the resolved OS color too; only storage represents an override.
    () => readStoredTheme() ?? 'auto',
  );

  useEffect(() => {
    if (preference !== 'auto') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const update = () => {
      const next = media.matches ? 'dark' : 'light';
      changeColor(next);
    };
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, [preference, changeColor]);

  // Keep the attribute in sync after a user toggle. Boot already applied the
  // resolved theme (and the head script did it before first paint), so this
  // must not write a different value on mount — that was a light↔dark flash.
  useLayoutEffect(() => {
    document.documentElement.dataset.season = activePackage.id;
    applyTheme(theme);
  }, [theme, activePackage]);

  // Persist the preference immediately; Home swaps colors and artwork together
  // after decoding and fading out. Other routes retain immediate switching.
  const setTheme = useCallback((next: Theme, options?: { persist?: boolean }) => {
    if (options?.persist !== false) storeTheme(next);
    setPreferenceState(next);
    changeColor(next);
  }, [changeColor]);

  const setPreference = useCallback(
    (next: Theme | 'auto') => {
      if (next !== 'auto') {
        setTheme(next);
        return;
      }
      clearStoredTheme();
      setPreferenceState('auto');
      const resolved = prefersDarkTheme() ? 'dark' : 'light';
      changeColor(resolved);
    },
    [setTheme, changeColor],
  );

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [setTheme, theme]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      season: activePackage.id,
      seasonPackage: activePackage,
      setSeasonOverride,
      setSeason,
      theme,
      setTheme,
      toggleTheme,
      preference,
      setPreference,
    }),
    [activePackage, setSeason, theme, setTheme, toggleTheme, preference, setPreference],
  );

  return (
    <ThemeContext.Provider value={value}>
      <style data-season-styles={activePackage.id}>{activePackage.css}</style>
      {children}
    </ThemeContext.Provider>
  );
}
